import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePageTracking, useMetricsIdentity } from '@byscott-io/metricsbyscott-web';
import {
  configureAuth,
  AuthProvider,
  useAuth,
  ThemeProvider,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  AcceptInvitationPage,
  ContainersPage,
  ProtectedRoute as AuthProtectedRoute,
  PublicRoute,
  MarketingRoute,
  PublicChat,
  useSpaLinkInterceptor,
  storage,
  FormGuardProvider,
  ErrorBoundary,
  UpdateAvailableBanner,
  VERSION_ANNOUNCED_EVENT,
} from '@byscott-io/corebyscott';
import { configureCable, PresenceProvider, useWaldoEvent } from '@byscott-io/where-is-waldo';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import AppsPage from './pages/AppsPage';
import AppDetailPage from './pages/AppDetailPage';
import AffiliatesPage from './pages/AffiliatesPage';
import AffiliateDetailPage from './pages/AffiliateDetailPage';
import ReferralCodesPage from './pages/ReferralCodesPage';
import ClientAccountsListPage from './pages/ClientAccountsListPage';
import ClientAccountDetailPage from './pages/ClientAccountDetailPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import LevelDetailPage from './pages/LevelDetailPage';
import FeaturesPage from './pages/FeaturesPage';
import QuotaPage from './pages/QuotaPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';

// Marketing pages
import LandingPage from './pages/marketing/LandingPage';
import MarketingFeaturesPage from './pages/marketing/FeaturesPage';
import PricingPage from './pages/marketing/PricingPage';
import AboutPage from './pages/marketing/AboutPage';
import ContactPage from './pages/marketing/ContactPage';
import HelpPage from './pages/marketing/HelpPage';

// Configure corebyscott for the billing/admin surface. Exported and called by
// application.js at MOUNT time for the surface that actually renders — NOT at
// import — so the affiliate surface's configureAuth can't clobber this shared
// corebyscott config singleton (and vice-versa). See #614 host partitioning.
export function configureBillingSurface() {
  configureAuth({
    appName: 'BillingByScott',
    containerName: 'account',
    containerLabel: 'Account',
    containerLabelPlural: 'Accounts',
    containerHeader: 'X-Account-Id',
    containerBasePath: '/app',
    dashboardPath: '/app',
    jwtIncludeContainer: true,
    roles: ['owner', 'admin', 'staff', 'member'],
    adminRoles: ['owner', 'admin'],
    // Bot-check config for anonymous chat (#521) — values come from
    // Rails-rendered meta tags in the layout.
    botCheckProvider:
      typeof document !== 'undefined'
        ? document.querySelector('meta[name="bot-check-provider"]')?.content || 'off'
        : 'off',
    turnstileSiteKey:
      typeof document !== 'undefined'
        ? document.querySelector('meta[name="turnstile-site-key"]')?.content || null
        : null,
    features: {
      invitations: true,
      passwordReset: true,
      sessionsManagement: true,
      multiContainer: true,
      registration: true,
      mfa: true,
    },
    footer: {
      enabled: true,
      brand: 'IdeasByScott',
      brandUrl: 'https://byscott.io',
      links: [],
    },
    marketing: {
      navItems: [
        { path: '/features', label: 'Features' },
        { path: '/pricing', label: 'Pricing' },
        { path: '/about', label: 'About' },
        { path: '/help', label: 'Help' },
        { path: '/contact', label: 'Contact' },
      ],
      footer: {
        companyName: 'IdeasByScott',
        companyDescription: 'Building practical tools for real-world problems.',
        productLinks: [
          { label: 'Features', href: '/features' },
          { label: 'Pricing', href: '/pricing' },
          { label: 'Help', href: '/help' },
        ],
        companyLinks: [
          { label: 'About', href: '/about' },
          { label: 'Contact', href: '/contact' },
        ],
        legalLinks: [
          { label: 'Privacy Policy', href: 'https://byscott.io/privacy-policy' },
          { label: 'Terms of Service', href: 'https://byscott.io/terms' },
        ],
        copyright: `© ${new Date().getFullYear()} IdeasByScott. All rights reserved.`,
      },
    },
    logo: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        {/* Invoice/receipt with dollar sign */}
        <path fillRule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V7.875L14.625 1.5H5.625ZM14.25 3.75v3.375c0 .621.504 1.125 1.125 1.125h3.375L14.25 3.75Z" clipRule="evenodd" />
        <path d="M12 10.5c-.69 0-1.25.56-1.25 1.25v.25h-.5a.75.75 0 000 1.5h.5v1h-.5a.75.75 0 000 1.5h.5v.25c0 .69.56 1.25 1.25 1.25s1.25-.56 1.25-1.25v-.25h.5a.75.75 0 000-1.5h-.5v-1h.5a.75.75 0 000-1.5h-.5v-.25c0-.69-.56-1.25-1.25-1.25Z" />
      </svg>
    ),
  });

  // Real-time cable connection (#626): JWT auth via corebyscott storage token.
  // Admin-only — the affiliate surface has no presence/cable — so keep it out of
  // import scope, same shared-config isolation as configureAuth above (#614).
  configureCable({
    url: '/cable',
    getToken: () => storage.getToken(),
  });
}

function PageTracker() {
  const location = useLocation();
  usePageTracking(location.pathname);
  return null;
}

function MetricsIdentity() {
  useMetricsIdentity(useAuth());
  return null;
}

function ProtectedRoute({ children }) {
  return (
    <AuthProtectedRoute>
      <Layout>{children}</Layout>
    </AuthProtectedRoute>
  );
}

function SpaLinkInterceptor({ children }) {
  useSpaLinkInterceptor();
  return children;
}

// TWO BOUNDARIES, doing different jobs. This inner one is inside the router and
// resets on navigation, so a page that throws is recovered by going elsewhere.
// The outer one in App sits OUTSIDE the providers, so it also catches a crash
// in a provider, which this one cannot -- it mounts underneath them. Without
// either, an uncaught render error unmounts the tree and leaves a blank page.
function RouteBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// A deploy announces itself over waldo when the new container boots. Core
// cannot subscribe for itself -- it must not import the waldo client, since not
// every app has one -- so the app turns the broadcast into the window event
// core listens for. The payload is not used: it only prompts a fresh ask of
// /api/version, which is what actually decides.
function VersionAnnouncements() {
  useWaldoEvent('app_version', () => {
    window.dispatchEvent(new Event(VERSION_ANNOUNCED_EVENT));
  });
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <PresenceProvider>
        <FormGuardProvider>
        <BrowserRouter>
          {/* Offers a refresh when a deploy has replaced the code this tab is
              running. Never reloads on its own. */}
          <UpdateAvailableBanner />
          <VersionAnnouncements />
          <PageTracker />
          <MetricsIdentity />
          <SpaLinkInterceptor>
          <RouteBoundary>
          <Routes>
          {/* Marketing Pages (public, no redirect for logged-in users) */}
          <Route
            path="/"
            element={
              <MarketingRoute>
                <LandingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/features"
            element={
              <MarketingRoute>
                <MarketingFeaturesPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <MarketingRoute>
                <PricingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/about"
            element={
              <MarketingRoute>
                <AboutPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/contact"
            element={
              <MarketingRoute>
                <ContactPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/help/*"
            element={
              <MarketingRoute>
                <HelpPage />
              </MarketingRoute>
            }
          />

          {/* Auth Pages (redirect logged-in users to /app) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginForm />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterForm />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/invitations/accept/:token"
            element={<AcceptInvitationPage />}
          />

          {/* App Pages (protected, requires auth) */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/accounts"
            element={
              <ProtectedRoute>
                <ContainersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps"
            element={
              <ProtectedRoute>
                <AppsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:id"
            element={
              <ProtectedRoute>
                <AppDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/affiliates"
            element={
              <ProtectedRoute>
                <AffiliatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/affiliates/:affiliateId"
            element={
              <ProtectedRoute>
                <AffiliateDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/clients"
            element={
              <ProtectedRoute>
                <ClientAccountsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/clients/:clientId"
            element={
              <ProtectedRoute>
                <ClientAccountDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/clients/:clientId/invoices/:invoiceId"
            element={
              <ProtectedRoute>
                <InvoiceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/products"
            element={
              <ProtectedRoute>
                <ProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/products/:productId"
            element={
              <ProtectedRoute>
                <ProductDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/products/:productId/levels/:levelId"
            element={
              <ProtectedRoute>
                <LevelDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/features"
            element={
              <ProtectedRoute>
                <FeaturesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/quota"
            element={
              <ProtectedRoute>
                <QuotaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/apps/:appId/referral_codes"
            element={
              <ProtectedRoute>
                <ReferralCodesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteBoundary>
        </SpaLinkInterceptor>
        <PublicChat
          authenticatedTask={{
            taskType: 'BillingByScottChatTask',
            title: 'Billing AI',
            placeholder: 'Ask about subscriptions, plans, or client accounts...',
            welcomeMessage:
              'Hi! I can help you understand subscriptions, plans, and client accounts.',
            voiceEnabled: true,
            attachmentsEnabled: true,
          }}
          anonymousTask={{
            taskType: 'SupportChatTask',
            title: 'BillingByScott AI',
            welcomeMessage:
              'Hi! I can help you figure out if BillingByScott fits your SaaS billing needs. What are you trying to solve?',
            placeholder: 'Ask about plans, integrations, vs Stripe…',
          }}
        />
      </BrowserRouter>
        </FormGuardProvider>
        </PresenceProvider>
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}
